import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../utils/AuthContext";
import { saveTestResults } from "../../utils/firebase";

const CSS = `
  .pf-results { min-height: 100vh; padding: 110px 0 80px; position: relative; }
  .pf-results-bg {
    position: absolute; inset: 0; pointer-events: none;
    background:
      radial-gradient(ellipse 50% 45% at 20% 30%, rgba(255,107,53,0.05) 0%, transparent 55%),
      radial-gradient(ellipse 40% 40% at 80% 70%, rgba(0,212,170,0.04) 0%, transparent 50%);
  }
  .pf-results-wrap { max-width: 860px; margin: 0 auto; padding: 0 24px; position: relative; z-index: 1; }

  /* Persona Card */
  .pf-persona {
    padding: 44px; border-radius: 28px; text-align: center;
    background: linear-gradient(135deg, rgba(255,107,53,0.06), rgba(124,92,252,0.04), rgba(0,212,170,0.03));
    border: 1px solid rgba(255,107,53,0.12);
    margin-bottom: 36px; position: relative; overflow: hidden;
    animation: fadeInUp 0.7s ease both;
  }
  .pf-persona::before {
    content: ''; position: absolute; top: 0; left: 15%; right: 15%; height: 2px;
    background: linear-gradient(90deg, transparent, rgba(255,107,53,0.5), transparent);
  }
  .pf-persona-emoji { font-size: 3.5em; margin-bottom: 12px; display: block; animation: float 3s ease-in-out infinite; }
  .pf-persona-title {
    font-family: 'DM Serif Display', serif; font-size: clamp(1.6em, 3.5vw, 2.2em);
    color: #F0F0F5; margin-bottom: 10px;
  }
  .pf-persona-desc {
    font-size: 0.92em; color: rgba(240,240,245,0.5); max-width: 520px;
    margin: 0 auto 24px; line-height: 1.75;
  }

  /* Chart Container */
  .pf-chart-wrap {
    max-width: 380px; margin: 0 auto 20px; position: relative;
    background: rgba(255,255,255,0.02); border-radius: 20px; padding: 24px;
    border: 1px solid rgba(255,255,255,0.04);
  }

  /* Trait Bars */
  .pf-trait-bars { display: flex; flex-direction: column; gap: 12px; max-width: 480px; margin: 0 auto; text-align: left; }
  .pf-trait-row { display: flex; align-items: center; gap: 14px; }
  .pf-trait-label { width: 85px; font-size: 0.78em; font-weight: 600; color: rgba(240,240,245,0.5); flex-shrink: 0; }
  .pf-trait-bar-bg { flex: 1; height: 10px; border-radius: 10px; background: rgba(255,255,255,0.04); overflow: hidden; }
  .pf-trait-bar-fill { height: 100%; border-radius: 10px; transition: width 1.5s cubic-bezier(0.22,1,0.36,1); }
  .pf-trait-pct { width: 40px; font-family: 'JetBrains Mono', monospace; font-size: 0.78em; font-weight: 600; text-align: right; }

  /* Stream Recommendation */
  .pf-stream-rec {
    padding: 28px; border-radius: 20px; margin-bottom: 36px;
    background: rgba(0,212,170,0.04); border: 1px solid rgba(0,212,170,0.12);
    animation: fadeInUp 0.6s ease 0.15s both;
  }
  .pf-stream-rec-h { display: flex; align-items: center; gap: 10px; margin-bottom: 12px; }
  .pf-stream-rec-icon { width: 40px; height: 40px; border-radius: 12px; display: flex; align-items: center; justify-content: center; font-size: 1.2em; background: rgba(0,212,170,0.1); border: 1px solid rgba(0,212,170,0.2); }
  .pf-stream-rec-title { font-weight: 800; font-size: 0.95em; color: #F0F0F5; }
  .pf-stream-rec-stream { font-family: 'DM Serif Display', serif; font-size: 1.3em; color: #00D4AA; margin-bottom: 8px; }
  .pf-stream-rec-reason { font-size: 0.85em; color: rgba(240,240,245,0.42); line-height: 1.7; }

  /* Saved Banner */
  .pf-saved-banner {
    display: flex; align-items: center; justify-content: center; gap: 8px;
    padding: 10px 20px; border-radius: 50px; margin-bottom: 28px;
    background: rgba(0,212,170,0.06); border: 1px solid rgba(0,212,170,0.15);
    font-size: 0.82em; font-weight: 600; color: #00D4AA;
    animation: fadeInUp 0.5s ease 0.1s both;
  }

  /* Career Matches */
  .pf-matches-title { font-family: 'DM Serif Display', serif; font-size: 1.6em; color: #F0F0F5; margin-bottom: 8px; text-align: center; }
  .pf-matches-sub { font-size: 0.86em; color: rgba(240,240,245,0.32); text-align: center; margin-bottom: 28px; }

  .pf-match-card {
    display: flex; align-items: stretch; border-radius: 20px;
    background: rgba(255,255,255,0.025); border: 1px solid rgba(255,255,255,0.06);
    overflow: hidden; margin-bottom: 16px;
    transition: all 0.4s cubic-bezier(0.22,1,0.36,1);
    animation: fadeInUp 0.6s ease both; cursor: pointer; text-decoration: none;
  }
  .pf-match-card:nth-child(1) { animation-delay: 0.2s; }
  .pf-match-card:nth-child(2) { animation-delay: 0.35s; }
  .pf-match-card:nth-child(3) { animation-delay: 0.5s; }
  .pf-match-card:nth-child(4) { animation-delay: 0.6s; }
  .pf-match-card:nth-child(5) { animation-delay: 0.7s; }
  .pf-match-card:hover {
    background: rgba(255,255,255,0.04); border-color: rgba(255,107,53,0.15);
    transform: translateY(-4px); box-shadow: 0 16px 40px rgba(0,0,0,0.2);
  }
  .pf-match-rank {
    width: 72px; flex-shrink: 0; display: flex; flex-direction: column;
    align-items: center; justify-content: center; padding: 16px 0; position: relative;
  }
  .pf-match-rank::after { content: ''; position: absolute; right: 0; top: 15%; bottom: 15%; width: 1px; background: rgba(255,255,255,0.05); }
  .pf-match-rank-num { font-family: 'DM Serif Display', serif; font-size: 1.6em; line-height: 1; }
  .pf-match-rank-label { font-size: 0.55em; font-weight: 600; color: rgba(240,240,245,0.3); letter-spacing: 1px; text-transform: uppercase; margin-top: 2px; }
  .pf-match-body { flex: 1; padding: 18px 20px; }
  .pf-match-emoji-row { display: flex; align-items: center; gap: 10px; margin-bottom: 4px; }
  .pf-match-emoji { font-size: 1.4em; }
  .pf-match-title { font-weight: 800; font-size: 1em; color: #F0F0F5; }
  .pf-match-tagline { font-size: 0.74em; color: rgba(240,240,245,0.3); }
  .pf-match-why {
    font-size: 0.78em; color: rgba(240,240,245,0.42); line-height: 1.6;
    margin-top: 8px; font-style: italic;
    padding: 8px 12px; border-radius: 10px; background: rgba(255,255,255,0.02);
    border-left: 2px solid rgba(255,107,53,0.2);
  }
  .pf-match-pct-wrap { display: flex; align-items: center; gap: 10px; margin-top: 10px; }
  .pf-match-pct-bar-bg { flex: 1; height: 6px; border-radius: 6px; background: rgba(255,255,255,0.04); overflow: hidden; }
  .pf-match-pct-bar { height: 100%; border-radius: 6px; transition: width 1.5s cubic-bezier(0.22,1,0.36,1); }
  .pf-match-pct-num { font-family: 'JetBrains Mono', monospace; font-size: 0.82em; font-weight: 700; min-width: 40px; text-align: right; }
  .pf-match-arrow { width: 44px; flex-shrink: 0; display: flex; align-items: center; justify-content: center; font-size: 1.1em; color: rgba(240,240,245,0.12); transition: all 0.3s ease; }
  .pf-match-card:hover .pf-match-arrow { color: #FF6B35; transform: translateX(4px); }

  /* Actions */
  .pf-results-actions { display: flex; justify-content: center; gap: 14px; margin-top: 40px; flex-wrap: wrap; }

  /* No Results */
  .pf-no-results { text-align: center; padding: 80px 0; }
  .pf-no-results-icon { font-size: 4em; margin-bottom: 20px; display: block; }
  .pf-no-results-title { font-family: 'DM Serif Display', serif; font-size: 1.8em; color: #F0F0F5; margin-bottom: 10px; }
  .pf-no-results-desc { color: rgba(240,240,245,0.4); margin-bottom: 28px; }

  @keyframes fadeInUp { from { opacity:0; transform:translateY(30px); } to { opacity:1; transform:translateY(0); } }
  @keyframes float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-10px)} }

  @media(max-width:600px) {
    .pf-match-card { flex-direction: column; }
    .pf-match-rank { width: 100%; flex-direction: row; gap: 8px; padding: 12px 20px; }
    .pf-match-rank::after { display: none; }
    .pf-match-arrow { display: none; }
    .pf-persona { padding: 28px 20px; }
  }
`;

const TRAIT_COLORS = {
  analytical: "#6C5CE7",
  creative: "#A29BFE",
  social: "#00D4AA",
  technical: "#00B894",
  business: "#FDCB6E",
};
const TRAIT_LABELS = {
  analytical: "Analytical",
  creative: "Creative",
  social: "Social",
  technical: "Technical",
  business: "Business",
};
const RANK_LABELS = ["BEST", "GREAT", "GOOD", "SOLID", "FAIR"];
const RANK_COLORS = ["#FFD23F", "#C0C0C0", "#CD7F32", "#00D4AA", "#A29BFE"];

/* ── Radar Chart drawn with Canvas API ── */
function RadarChart({ traits }) {
  const canvasRef = useRef(null);
  const keys = ["analytical", "creative", "social", "technical", "business"];
  const labels = ["Analytical", "Creative", "Social", "Technical", "Business"];
  const colors = ["#6C5CE7", "#A29BFE", "#00D4AA", "#00B894", "#FDCB6E"];

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const W = 340,
      H = 340;
    canvas.width = W * 2;
    canvas.height = H * 2;
    canvas.style.width = W + "px";
    canvas.style.height = H + "px";
    ctx.scale(2, 2);

    const cx = W / 2,
      cy = H / 2,
      R = 120;
    const n = keys.length;
    const angleStep = (2 * Math.PI) / n;
    const startAngle = -Math.PI / 2;

    ctx.clearRect(0, 0, W, H);

    // Background rings
    [1, 0.75, 0.5, 0.25].forEach((scale) => {
      ctx.beginPath();
      for (let i = 0; i < n; i++) {
        const angle = startAngle + i * angleStep;
        const x = cx + R * scale * Math.cos(angle);
        const y = cy + R * scale * Math.sin(angle);
        i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
      }
      ctx.closePath();
      ctx.strokeStyle = "rgba(255,255,255,0.06)";
      ctx.lineWidth = 1;
      ctx.stroke();
    });

    // Axis lines
    for (let i = 0; i < n; i++) {
      const angle = startAngle + i * angleStep;
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.lineTo(cx + R * Math.cos(angle), cy + R * Math.sin(angle));
      ctx.strokeStyle = "rgba(255,255,255,0.04)";
      ctx.stroke();
    }

    // Data polygon
    const values = keys.map((k) => (traits[k] || 0) / 100);
    ctx.beginPath();
    values.forEach((v, i) => {
      const angle = startAngle + i * angleStep;
      const x = cx + R * v * Math.cos(angle);
      const y = cy + R * v * Math.sin(angle);
      i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
    });
    ctx.closePath();
    const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, R);
    grad.addColorStop(0, "rgba(255,107,53,0.25)");
    grad.addColorStop(1, "rgba(0,212,170,0.08)");
    ctx.fillStyle = grad;
    ctx.fill();
    ctx.strokeStyle = "rgba(255,107,53,0.6)";
    ctx.lineWidth = 2;
    ctx.stroke();

    // Data points + labels
    values.forEach((v, i) => {
      const angle = startAngle + i * angleStep;
      const px = cx + R * v * Math.cos(angle);
      const py = cy + R * v * Math.sin(angle);
      ctx.beginPath();
      ctx.arc(px, py, 4, 0, Math.PI * 2);
      ctx.fillStyle = colors[i];
      ctx.fill();
      ctx.strokeStyle = "#0A0E1A";
      ctx.lineWidth = 2;
      ctx.stroke();
      const lx = cx + (R + 28) * Math.cos(angle);
      const ly = cy + (R + 28) * Math.sin(angle);
      ctx.font = "600 11px 'Plus Jakarta Sans', sans-serif";
      ctx.fillStyle = "rgba(240,240,245,0.5)";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(labels[i], lx, ly - 7);
      ctx.font = "700 13px 'JetBrains Mono', monospace";
      ctx.fillStyle = colors[i];
      ctx.fillText(traits[keys[i]] + "%", lx, ly + 8);
    });
  }, [traits]);

  return (
    <canvas ref={canvasRef} style={{ display: "block", margin: "0 auto" }} />
  );
}

export default function Results() {
  const [results, setResults] = useState(null);
  const [saved, setSaved] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  // Load results from sessionStorage
  useEffect(() => {
    const stored = sessionStorage.getItem("pf-results");
    if (stored) setResults(JSON.parse(stored));
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // Auto-save to Firebase when user is logged in
  useEffect(() => {
    async function saveToFirebase() {
      if (user && results && !saved) {
        try {
          await saveTestResults(user.uid, results);
          setSaved(true);
        } catch (err) {
          console.error("Failed to save results:", err);
        }
      }
    }
    saveToFirebase();
  }, [user, results, saved]);

  if (!results) {
    return (
      <>
        <style>{CSS}</style>
        <section className="pf-results">
          <div className="pf-results-wrap">
            <div className="pf-no-results">
              <span className="pf-no-results-icon">🧭</span>
              <h1 className="pf-no-results-title">No Results Yet</h1>
              <p className="pf-no-results-desc">
                Take the career aptitude test first to see your personalized
                results.
              </p>
              <Link to="/test" className="btn-primary">
                🚀 Take the Test
              </Link>
            </div>
          </div>
        </section>
      </>
    );
  }

  const { traits, careers, personality } = results;

  return (
    <>
      <style>{CSS}</style>
      <section className="pf-results">
        <div className="pf-results-bg" />
        <div className="pf-results-wrap">
          {/* Saved confirmation banner */}
          {saved && (
            <div className="pf-saved-banner">
              ✅ Results saved to your account! View them anytime in your{" "}
              <Link
                to="/dashboard"
                style={{ color: "#00D4AA", textDecoration: "underline" }}
              >
                Dashboard
              </Link>
            </div>
          )}

          {/* ── Personality Card ── */}
          <div className="pf-persona">
            <div className="section-badge" style={{ margin: "0 auto 16px" }}>
              🎉 Your Results
            </div>
            <span className="pf-persona-emoji">{personality.emoji}</span>
            <h1 className="pf-persona-title">
              You are <span className="gradient-text">{personality.title}</span>
            </h1>
            <p className="pf-persona-desc">{personality.description}</p>

            {/* ── Radar Chart ── */}
            <div className="pf-chart-wrap">
              <RadarChart traits={traits} />
            </div>

            {/* ── Trait Bars ── */}
            <div className="pf-trait-bars">
              {Object.entries(traits)
                .sort((a, b) => b[1] - a[1])
                .map(([trait, pct]) => (
                  <div className="pf-trait-row" key={trait}>
                    <div className="pf-trait-label">{TRAIT_LABELS[trait]}</div>
                    <div className="pf-trait-bar-bg">
                      <div
                        className="pf-trait-bar-fill"
                        style={{
                          width: `${pct}%`,
                          background: `linear-gradient(90deg, ${TRAIT_COLORS[trait]}, ${TRAIT_COLORS[trait]}88)`,
                        }}
                      />
                    </div>
                    <div
                      className="pf-trait-pct"
                      style={{ color: TRAIT_COLORS[trait] }}
                    >
                      {pct}%
                    </div>
                  </div>
                ))}
            </div>
          </div>

          {/* ── Stream Recommendation ── */}
          <div className="pf-stream-rec">
            <div className="pf-stream-rec-h">
              <div className="pf-stream-rec-icon">📚</div>
              <div className="pf-stream-rec-title">
                Recommended Stream After 10th
              </div>
            </div>
            <div className="pf-stream-rec-stream">{personality.stream}</div>
            <div className="pf-stream-rec-reason">
              {personality.streamReason}
            </div>
          </div>

          {/* ── Career Matches ── */}
          <h2 className="pf-matches-title">Your Top Career Matches</h2>
          <p className="pf-matches-sub">
            Careers ranked by how well they match YOUR personality profile
          </p>

          {careers.slice(0, 5).map((career, i) => (
            <Link
              to={`/careers/${career.id}`}
              className="pf-match-card"
              key={career.id}
            >
              <div className="pf-match-rank">
                <div
                  className="pf-match-rank-num"
                  style={{ color: RANK_COLORS[i] }}
                >
                  #{i + 1}
                </div>
                <div className="pf-match-rank-label">
                  {RANK_LABELS[i]} Match
                </div>
              </div>
              <div className="pf-match-body">
                <div className="pf-match-emoji-row">
                  <span className="pf-match-emoji">{career.emoji}</span>
                  <div>
                    <span className="pf-match-title">{career.title}</span>
                    <div className="pf-match-tagline">{career.tagline}</div>
                  </div>
                </div>
                {career.whyMatch && (
                  <div className="pf-match-why">💡 {career.whyMatch}</div>
                )}
                <div className="pf-match-pct-wrap">
                  <div className="pf-match-pct-bar-bg">
                    <div
                      className="pf-match-pct-bar"
                      style={{
                        width: `${career.matchPercentage}%`,
                        background: `linear-gradient(90deg, ${career.color}, ${career.color}88)`,
                      }}
                    />
                  </div>
                  <div
                    className="pf-match-pct-num"
                    style={{ color: career.color }}
                  >
                    {career.matchPercentage}%
                  </div>
                </div>
              </div>
              <div className="pf-match-arrow">→</div>
            </Link>
          ))}

          {/* ── Actions ── */}
          <div className="pf-results-actions">
            {!user && (
              <Link
                to="/signup"
                className="btn-primary"
                style={{
                  background: "linear-gradient(135deg, #00D4AA, #00B894)",
                }}
              >
                💾 Sign Up to Save Results
              </Link>
            )}
            <Link to="/careers" className="btn-primary">
              📚 Explore All Careers
            </Link>
            <Link to="/streams" className="btn-secondary">
              🗺️ Stream Guide
            </Link>
            <button
              className="btn-secondary"
              onClick={() => {
                sessionStorage.removeItem("pf-results");
                setSaved(false);
                navigate("/test");
              }}
            >
              🔄 Retake Test
            </button>
          </div>
        </div>
      </section>
    </>
  );
}
